import { BasicRow, Modal } from "@easyblocks/design-system";
import { EasyblocksEditor, useEditorContext } from "@easyblocks/editor";
import { ChangeEvent, useEffect, useState } from "react";
import "./editor-overrides.css";
import { easyblocksConfig } from "../utils/easyblocks-config/index";
import { easyblocksComponents } from "../components";
import { QuillTextEditor } from "../components/primitives/quill-text-editor";
import { resolveExternalData } from "../api";
import { resolveExternalDataLocal } from "../api/adapters/local-mocks";
import { localBackend } from "../api/adapters/local-backend";
import { getSelectedBackend } from "../utils/easyblocks-config/config";
import { NewsPickerWidget } from "../widgets/NewsPickerWidget";
import { EventsPickerWidget } from "../widgets/EventsPickerWidget";
import { ImageUploadWidget } from "../widgets/ImageUploadWidget";
import { DocumentUploadWidget } from "../widgets/DocumentUploadWidget";
import { DocumentCategoryWidget } from "../widgets/DocumentCategoryWidget";
import { DocumentYearWidget } from "../widgets/DocumentYearWidget";
import { AlbumWidget } from "../widgets/AlbumWidget";
import { RosterRowFilterWidget } from "../widgets/RosterRowFilterWidget";
import { BackgroundColorWidget } from "../widgets/BackgroundColorWidget";
import { ButtonColorWidget } from "../widgets/ButtonColorWidget";
import { BorderColorWidget } from "../widgets/BorderColorWidget";
import { TextColorWidget } from "../widgets/TextColorWidget";
import { EditorControls } from "../components/EditorControls";
import { useAppStore } from "../state/app-store";
import { useShallow } from "zustand/react/shallow";
import type { ExternalData } from "@easyblocks/core";

const CARD_IMAGE_HIDDEN_FIELD_PATHS = [
    "horizontalAlignClass",
    "paddingClass",
    "border",
    "borderWidth",
    "borderColorClass",
    "borderRadius",
] as const;

function dotNotationGet(obj: unknown, path: string): unknown {
    if (!path) return obj;

    return path.split(".").reduce<unknown>((acc, key) => {
        if (!acc || typeof acc !== "object") return undefined;
        return (acc as Record<string, unknown>)[key];
    }, obj);
}

function matchesFieldName(fieldName: string, prop: string) {
    return fieldName
        .split(",")
        .map((name) => name.trim())
        .some((name) => name === prop || name.endsWith(`.${prop}`));
}

function isCardImageSelection(editorContext: any) {
    const focussedField = editorContext?.focussedField;
    const formValues = editorContext?.form?.values;

    if (!Array.isArray(focussedField) || focussedField.length !== 1 || !formValues) {
        return false;
    }

    const selectedPath = focussedField[0];
    const selectedEntry = dotNotationGet(formValues, selectedPath) as { _component?: string } | undefined;

    if (!selectedEntry || (selectedEntry._component !== "ImageBlock" && selectedEntry._component !== "CardImageBlock")) {
        return false;
    }

    const lastDotIndex = selectedPath.lastIndexOf(".");
    if (lastDotIndex < 0) {
        return false;
    }

    const parentPath = selectedPath.slice(0, lastDotIndex);
    const slotName = selectedPath.slice(lastDotIndex + 1);
    const parentEntry = dotNotationGet(formValues, parentPath) as { _component?: string } | undefined;

    return slotName === "Image" && parentEntry?._component === "Card";
}

type EditorAppProps = {
    onExitToRender?: () => void;
};

type PickerTemplate = {
    id: string;
    label?: string;
    thumbnail?: string;
    isUserDefined?: boolean;
    [key: string]: any;
};

type PickerComponent = {
    id: string;
    label?: string;
    thumbnail?: string;
};

type PickerTemplatesDictionary = Record<
    string,
    {
        component: PickerComponent;
        templates: PickerTemplate[];
    }
>;

type CompactPickerProps = {
    isOpen: boolean;
    templates?: PickerTemplatesDictionary;
    onClose: (template?: PickerTemplate) => void;
    mode?: string;
};

const ROOT_SECTION_COMPONENT_IDS = new Set([
    "HeroImageSection",
    "SingleColumn",
    "ColumnsSection",
    "GridSection",
    "CTASection",
    "AccordionSection",
    "VideoEmbedSection",
    "NewsLatestRow",
    "EventsRow",
    "PhotoAlbum",
    "RosterAccordion",
    "NeighborhoodsAccordion",
    "ResidentDirectorySection",
    "DocumentsAccordion",
    "ListTemplateSection",
]);

function checkQueryForTemplate(query: string, template: PickerTemplate, component: PickerComponent) {
    return `${template.label ?? ""}${component.label ?? component.id}`
        .toLocaleLowerCase()
        .includes(query.trim().toLocaleLowerCase());
}

function isSectionPicker(templates?: PickerTemplatesDictionary) {
    const componentIds = Object.keys(templates ?? {});
    return componentIds.length > 0 && componentIds.every((id) => ROOT_SECTION_COMPONENT_IDS.has(id));
}

function ensureSectionInName(label: string) {
    const trimmedLabel = label.trim();
    const autoSuffix = " (Auto)";
    const hasAutoSuffix = trimmedLabel.endsWith(autoSuffix);
    const labelWithoutAutoSuffix = hasAutoSuffix
        ? trimmedLabel.slice(0, -autoSuffix.length)
        : trimmedLabel;
    const separator = " — ";
    const separatorIndex = labelWithoutAutoSuffix.indexOf(separator);
    const namePart = separatorIndex >= 0
        ? labelWithoutAutoSuffix.slice(0, separatorIndex).trim()
        : labelWithoutAutoSuffix.trim();
    const descriptionPart = separatorIndex >= 0
        ? labelWithoutAutoSuffix.slice(separatorIndex + separator.length).trim()
        : "";
    const normalizedNamePart = /\bsection\b/i.test(namePart)
        ? namePart
        : `${namePart} Section`;

    return `${normalizedNamePart}${descriptionPart ? `${separator}${descriptionPart}` : ""}${hasAutoSuffix ? autoSuffix : ""}`;
}

function CompactComponentPicker({ onClose, templates, isOpen }: CompactPickerProps) {
    const editorContext = useEditorContext();
    const [query, setQuery] = useState("");
    const trimmedQuery = query.trim().toLocaleLowerCase();
    const filteredTemplatesDict: PickerTemplatesDictionary = {};
    const sectionPicker = isSectionPicker(templates);

    if (templates) {
        Object.values(templates).forEach(({ templates: groupTemplates, component }) => {
            const filteredTemplates =
                trimmedQuery === ""
                    ? groupTemplates
                    : groupTemplates.filter((template) => checkQueryForTemplate(trimmedQuery, template, component));

            if (filteredTemplates.length > 0) {
                filteredTemplatesDict[component.id] = {
                    component,
                    templates: filteredTemplates,
                };
            }
        });
    }

    const close = (template?: PickerTemplate) => {
        setQuery("");
        onClose(template);
    };

    return (
        <Modal
            mode="center-small"
            isOpen={isOpen}
            onRequestClose={() => close(undefined)}
            noPadding={true}
            headerLine={true}
            title={sectionPicker ? "Choose a Section to Add" : "Choose a Component to Add"}
            searchProps={{
                value: query,
                placeholder: "Search...",
                onChange: (e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value),
            }}
            headerSymbol=""
        >
            {templates === undefined && "Loading..."}
            {templates !== undefined &&
                Object.entries(filteredTemplatesDict).map(([, { templates: groupTemplates, component }]) => {
                    const isOnlyOne = groupTemplates.length === 1;
                    const componentLabel = component.label ?? component.id;
                    const sectionComponentLabel = sectionPicker
                        ? ensureSectionInName(componentLabel)
                        : componentLabel;

                    return groupTemplates.map((template) => {
                        const templateLabel = template.label ?? template.id;
                        const title = isOnlyOne
                            ? sectionComponentLabel
                            : templateLabel;
                        const thumbnail = template.thumbnail ?? component.thumbnail;
                        const description = isOnlyOne ? undefined : sectionComponentLabel;

                        return (
                            <BasicRow
                                key={template.id}
                                title={title}
                                description={description}
                                onClick={() => close(template)}
                                image={thumbnail}
                                tinyDescription={true}
                                onEdit={
                                    template.isUserDefined
                                        ? () => {
                                              editorContext.actions.openTemplateModal({
                                                  mode: "edit",
                                                  template,
                                              });
                                          }
                                        : undefined
                                }
                            />
                        );
                    });
                })}
        </Modal>
    );
}

const easyblocksComponentsEditor = {
    ...easyblocksComponents,
    "QuillText.editor": QuillTextEditor,
};

export function EditorApp({
    onExitToRender,
}: EditorAppProps) {
    const {
        externalData,
        editorChromeMode,
        isInIframe,
        setExternalData,
    } = useAppStore(useShallow((state) => ({
        externalData: state.externalData,
        editorChromeMode: state.editorChromeMode,
        isInIframe: state.isInIframe,
        setExternalData: state.setExternalData,
    })));

    useEffect(() => {
        if (typeof document === "undefined") return;

        const scrollSelectedItemsIntoView = (root: ParentNode | Element = document) => {
            const listboxes: Element[] = [];

            if (root instanceof Element && root.matches('[role="listbox"]')) {
                listboxes.push(root);
            }

            if ("querySelectorAll" in root) {
                listboxes.push(...Array.from(root.querySelectorAll('[role="listbox"]')));
            }

            listboxes.forEach((listbox) => {
                const selectedItem = listbox.querySelector<HTMLElement>(
                    '[data-state="checked"], [aria-selected="true"], [data-selected="true"]'
                );

                selectedItem?.scrollIntoView({ block: "nearest" });
            });
        };

        const observer = new MutationObserver((mutations) => {
            const rootsToScan = new Set<Element>();

            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof Element)) return;

                    if (node.matches('[role="listbox"]')) {
                        rootsToScan.add(node);
                    }

                    node.querySelectorAll('[role="listbox"]').forEach((listbox) => {
                        rootsToScan.add(listbox);
                    });
                });
            });

            if (!rootsToScan.size) return;

            window.requestAnimationFrame(() => {
                rootsToScan.forEach((root) => {
                    scrollSelectedItemsIntoView(root);
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (typeof document === "undefined" || typeof window === "undefined") return;

        let frameId: number | null = null;

        const syncCardImageSidebarFields = () => {
            frameId = null;

            const editorContext = (window as typeof window & {
                editorWindowAPI?: { editorContext?: any };
            }).editorWindowAPI?.editorContext;

            const cardImageSelected = isCardImageSelection(editorContext);
            document.body.dataset.cardImageSelected = cardImageSelected ? "true" : "false";

            const sidebarRoot = document.getElementById("sidebar-panels-root");
            if (!sidebarRoot) return;

            const fieldWrappers = sidebarRoot.querySelectorAll<HTMLElement>(
                '[class^="wrapFieldWithMeta__FieldWrapper-"], [class*=" wrapFieldWithMeta__FieldWrapper-"]'
            );

            fieldWrappers.forEach((wrapper) => {
                const label = wrapper.querySelector<HTMLLabelElement>("label[for]");
                const fieldName = label?.htmlFor ?? "";
                const shouldHide =
                    cardImageSelected &&
                    CARD_IMAGE_HIDDEN_FIELD_PATHS.some((prop) => matchesFieldName(fieldName, prop));

                if (shouldHide) {
                    wrapper.setAttribute("data-card-image-field-hidden", "true");
                } else {
                    wrapper.removeAttribute("data-card-image-field-hidden");
                }
            });
        };

        const scheduleSync = () => {
            if (frameId !== null) return;
            frameId = window.requestAnimationFrame(syncCardImageSidebarFields);
        };

        const observer = new MutationObserver(() => {
            scheduleSync();
        });

        observer.observe(document.body, { childList: true, subtree: true });
        document.addEventListener("pointerup", scheduleSync, true);
        document.addEventListener("keyup", scheduleSync, true);
        window.addEventListener("focus", scheduleSync);

        scheduleSync();

        return () => {
            observer.disconnect();
            document.removeEventListener("pointerup", scheduleSync, true);
            document.removeEventListener("keyup", scheduleSync, true);
            window.removeEventListener("focus", scheduleSync);

            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }

            delete document.body.dataset.cardImageSelected;
            document
                .querySelectorAll<HTMLElement>("[data-card-image-field-hidden='true']")
                .forEach((wrapper) => wrapper.removeAttribute("data-card-image-field-hidden"));
        };
    }, []);

    let showChrome = !isInIframe;
    if (editorChromeMode === "full") {
        showChrome = true;
    } else if (editorChromeMode === "none") {
        showChrome = false;
    }

    const handleExternalDataChange = async (externals: any) => {
        const isLocal = getSelectedBackend() === localBackend;
        const res = isLocal
            ? resolveExternalDataLocal({ externals })
            : await resolveExternalData({ externals });
        const nextExternalData = res.externalData ?? {};

        // Defer store updates to avoid setState during Easyblocks render.
        queueMicrotask(() => {
            setExternalData((prev: ExternalData) => {
                const next = { ...prev, ...nextExternalData };
                const prevKeys = Object.keys(prev);
                const nextKeys = Object.keys(next);
                if (prevKeys.length !== nextKeys.length) return next;
                for (const key of nextKeys) {
                    if (prev[key] !== next[key]) return next;
                }
                return prev;
            });
        });
    };

    const editor = (
        <EasyblocksEditor
            config={easyblocksConfig as any}
            components={easyblocksComponentsEditor}
            pickers={{
                compact: CompactComponentPicker,
            }}
            widgets={{
                news: NewsPickerWidget,
                events: EventsPickerWidget,
                media: ImageUploadWidget,
                document: DocumentUploadWidget,
                documentCategory: DocumentCategoryWidget,
                documentYear: DocumentYearWidget,
                album: AlbumWidget,
                rosterRowFilter: RosterRowFilterWidget,
                backgroundColor: BackgroundColorWidget,
                buttonColor: ButtonColorWidget,
                borderColor: BorderColorWidget,
                textColor: TextColorWidget,
            }}
            externalData={externalData}
            onExternalDataChange={handleExternalDataChange}
        />
    );

    if (!showChrome) return editor;

    return (
        <div className="d-flex flex-column flex-fill">
            <EditorControls onSaveComplete={onExitToRender} onDiscardComplete={onExitToRender} />
            {editor}
        </div>
    );
}
